import { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { AfterCellChanged, Rows } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NAME,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TABLE_TERMS,
} from 'pages/FundInvestments/common/constants';
import {
  createColumns,
  createParser,
  CreateParserParams,
  createReverseParser,
  createRows,
  FundInvestmentSummaryColumn,
  ReverseParserParams,
} from './config';
import {
  FundInvestmentSummaryCellData,
  UseFundInvestmentSummarySheetParams,
  UseFundInvestmentSummarySheetResult,
} from './useFundInvestmentSummarySheet.types';

const useFundInvestmentSummarySheet = (
  params: UseFundInvestmentSummarySheetParams
): UseFundInvestmentSummarySheetResult => {
  const {
    fieldAttributes,
    forceReadOnly,
    fundInvestmentDetails,
    fundOptions,
    measurementDate,
    setFundInvestmentDetails,
  } = params;

  const rows = useMemo<Rows>(
    () => createRows({ forceReadOnly, fundInvestmentDetails, fundOptions, measurementDate }),
    [forceReadOnly, fundInvestmentDetails, fundOptions, measurementDate]
  );

  const columns = useMemo<FundInvestmentSummaryColumn[]>(() => {
    if (!isEmpty(fundInvestmentDetails)) {
      return createColumns({ fundInvestmentDetails });
    }

    return [];
  }, [fundInvestmentDetails]);

  const parser = useCallback(
    (props: CreateParserParams) =>
      createParser({
        ...props,
        forceReadOnly,
      }),
    [forceReadOnly]
  );

  const afterCellChanged = useCallback<AfterCellChanged<FundInvestmentSummaryCellData>>(initialChanges => {
    const changes = [...initialChanges];

    return changes;
  }, []);

  const reverseParser = useCallback(
    (reverseParserParams: ReverseParserParams) =>
      createReverseParser({
        ...reverseParserParams,
        setFundInvestmentDetails,
      }),
    [setFundInvestmentDetails]
  );

  // Spreadsheet config
  const spreadsheet = useMemo<SpreadsheetConfig | null>(() => {
    if (!isEmpty(columns)) {
      const spreadsheetConfig = new SpreadsheetConfig({
        afterCellChanged: afterCellChanged as SpreadsheetConfig['afterCellChanged'],
        allowConfirmAndDeleteColumn: false,
        allowDeleteRow: true,
        alwaysDisplayLegend: false,
        colTitleRow: 0,
        columns: columns as SpreadsheetConfig['columns'],
        currencyFormatter: true,
        fieldAttributes,
        format: undefined,
        hasColTitle: true,
        name: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NAME,
        page: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NAME as SpreadsheetConfig['page'],
        parser: parser as unknown as SpreadsheetConfig['parser'],
        reverseParser: reverseParser as SpreadsheetConfig['reverseParser'],
        rowConfig: rows as SpreadsheetConfig['rowConfig'],
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TABLE_TERMS as SpreadsheetConfig['tableTerms'],
        totalParser: undefined,
        unitsFormatter: true,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [afterCellChanged, columns, fieldAttributes, parser, reverseParser, rows]);

  return {
    spreadsheet,
  };
};

export default useFundInvestmentSummarySheet;
